/**
 * The Third Day — dealing and wording.
 *
 * Kept out of the component so a run can be dealt and checked without a
 * canvas, and so there is one place that decides what a run is called.
 */

import { WALK_QUESTIONS, WALK_LIVES, WALK_STEPS } from '../data/walkQuestions.js';

/** Fisher–Yates. `sort(() => Math.random() - 0.5)` is not a shuffle. */
function shuffled(list) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Deal one walk.
 *
 * More questions than there are steps: a wrong answer costs a life and moves
 * on to a *different* question, so a walk can use up to one per step plus one
 * per life. It used to re-ask the question you had just been shown the answer
 * to, which made every mistake a free step.
 *
 * The options are shuffled as well as the questions. Without that, the answer
 * is always in the same place and the walk becomes a test of noticing rather
 * than of knowing.
 */
export function dealWalk(pool = WALK_QUESTIONS, count = WALK_STEPS + WALK_LIVES) {
  return shuffled(pool)
    .slice(0, count)
    .map((q) => ({
      ...q,
      options: shuffled(q.a.map((text, i) => ({ text, ok: i === q.right }))),
    }));
}

/**
 * What a finished walk is worth.
 *
 * Reaching the garden is the point, so the score rewards *how* you got there
 * rather than only that you did: every step is worth the same, and lives still
 * in hand at the end are worth more than steps.
 */
export function scoreWalk(steps, livesLeft) {
  return steps * 60 + livesLeft * 80;
}

const RANKS = [
  { at: 720, name: 'Straight down the middle of it' },
  { at: 620, name: 'Barely broke stride' },
  { at: 540, name: 'Stopped once' },
  { at: 400, name: 'Made it through' },
  { at: 0, name: 'Turned back' },
];

export function rankWalk(score) {
  return RANKS.find((rank) => score >= rank.at).name;
}

/** Something worth pasting somewhere. */
export function shareWalk(steps, livesLeft, score) {
  const lives = '🟥'.repeat(livesLeft) + '⬛'.repeat(Math.max(0, WALK_LIVES - livesLeft));
  const reached = steps >= WALK_STEPS;
  return [
    `The Third Day — ${reached ? 'reached the garden' : `${steps}/${WALK_STEPS} steps`}`,
    lives,
    reached ? `${score} · ${rankWalk(score)}` : null,
    'Matthew 28:6',
    'scripturejourney.net',
  ]
    .filter(Boolean)
    .join('\n');
}

export { WALK_STEPS, WALK_LIVES };
